<script lang="ts" setup>
import { loadScript, type PayPalCardFieldsComponent, type PayPalNamespace } from '@paypal/paypal-js';
import { useSdk } from '~/sdk';
import { SfLoaderCircular } from '@storefront-ui/vue';
import Button from '~/components/ui/button/Button.vue';

type Module = { module_name: string; additionalInformation: string };

const props = defineProps<{ currentGateway: Module }>();
const { data } = useBootstrap();
const error = ref<any>(null);
const loading = ref(false);

const resultMessage = ref('');
let cardFields: PayPalCardFieldsComponent | null = null;

const { t } = useI18n();
const agreed = ref(false);

const privacyDescription = computed(() => {
  const privacyTextLong = t('I agree to the terms of service and will adhere to them unconditionally.');
  const privacyTextShort = t('terms of service').toLocaleLowerCase();
  return privacyTextLong.replace(
    privacyTextShort,
    `<a target="_blank" class='text-indigo-500' href='/terms-of-sale'>${privacyTextShort}</a>`,
  );
});

const paypalConfigRegex = /src', 'https:\/\/www\.paypal\.com\/sdk\/js?(.*?)'\)\;/;
const camelize = (s: string) => s.replace(/-./g, (x) => x[1]!.toUpperCase());

onMounted(async () => {
  if (!props.currentGateway) {
    error.value = 'Please select a payment method';
  }

  let matches: any = paypalConfigRegex.exec(props.currentGateway?.additionalInformation);

  let info: any = [...new URLSearchParams(matches[1]).entries()].reduce(function (acc, [key, value]) {
    let val = value.indexOf(',') > -1 ? value.split(',') : value;
    return { [camelize(key)]: val, ...acc };
  }, {});

  try {
    const paypal = (await loadScript({
      ...info,
      disableFunding: ['bancontact', 'sepa'],
      enableFunding: ['paylater'],
      components: 'buttons,card-fields',
    })) as any as PayPalNamespace;

    if (!paypal) {
      error.value = 'PayPal SDK failed to load';
      return;
    }

    // @ts-ignore: Object is possibly 'null'.
    await paypal
      .Buttons({
        onInit: () => (loading.value = false),
        createOrder: onCreateOrder,
        onApprove: onApprove,
        onCancel: function () {
          loading.value = false;
          error.value = 'paypal: Payment cancelled';
        },
        onError: function (err: any) {
          loading.value = false;
          error.value = 'paypal: An error occurred with PayPal payment: ' + err;
        },
        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'pill',
          label: 'paypal',
        },
      })
      .render('#paypal-button-container');

    if (paypal.CardFields) {
      // Create the card fields
      cardFields = paypal.CardFields({
        createOrder: onCreateOrder,
        onApprove: onApprove,
        onError: function (err) {
          error.value = 'card-fields: An error occurred with card payment: ' + err;
        },
      }) as any as PayPalCardFieldsComponent;

      if (!cardFields.isEligible()) {
        error.value = 'Card fields failed to load';
        return;
      }

      await cardFields.NameField({ placeholder: t('cardfields.name') }).render('#card-name-field-container');
      await cardFields.NumberField({ placeholder: t('cardfields.number') }).render('#card-number-field-container');
      await cardFields.ExpiryField({ placeholder: t('cardfields.expiry') }).render('#card-expiry-field-container');
      await cardFields.CVVField({ placeholder: t('cardfields.cvv') }).render('#card-cvv-field-container');
    }
  } catch (e: any) {
    error.value = 'general: ' + e.message;
  }
});

async function onCreateOrder(): Promise<string> {
  loading.value = true;
  const res: any = await useSdk().commerce.orderCreate({
    module: 'paypal',
    params: {
      page: 'payment-step',
      addAddress: 'true',
    },
  });

  if (!res.idOrder) {
    error.value = 'Failed to create order';
    throw new Error('Failed to create order');
  }

  return res.idOrder;
}

async function onApprove(data: any): Promise<void> {
  let res: any = await useSdk().commerce.orderUpdate(data);
  loading.value = false;
  if (res.success) {
    location.href = res.redirectUrl as unknown as string;
  } else {
    error.value = res.error;
  }
}

const handleCardPayment = async () => {
  try {
    if (!cardFields) {
      error.value = 'Card fields not initialized';
      return;
    }

    await cardFields.submit();
  } catch (e: any) {
    error.value = 'Error processing card payment: ' + e.message;
  }
};
</script>

<template>
  <div class="py-4">
    <Alert v-if="error" variant="destructive" class="mb-4 w-full">
      <AlertDescription>
        {{ error }}
      </AlertDescription>
    </Alert>

    <div class="relative flex items-start">
      <div class="flex h-6 items-center">
        <Checkbox v-model="agreed" id="privacy" aria-describedby="privacy-description" name="privacy" type="checkbox" />
      </div>
      <div class="ml-3 leading-6">
        <label for="privacy" class="font-medium text-gray-900">Privacy</label>
        <p v-html="privacyDescription" id="privacy-description" class="text-gray-500 text-sm"></p>
      </div>
    </div>

    <div class="my-6" v-if="!agreed">
      <Alert class="w-full !justify-start" variant="destructive">
        <AlertTitle>{{ $t('Please make sure') }}</AlertTitle>
        <AlertDescription>
          <ul role="list" class="list-disc space-y-1 pl-5 my-2">
            <li>{{ $t("You've chosen a payment method") }}</li>
            <li>{{ $t('Accepted the terms and conditions') }}</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>

    <div class="flex flex-col items-center justify-center">
      <div v-if="loading">
        <SfLoaderCircular size="lg" />
      </div>

      <div class="relative shrink-0 w-full">
        <div class="mt-8" :class="{ 'opacity-50 pointer-events-none': !agreed || loading }">
          <div id="paypal-button-container" class="paypal-button-container"></div>
          <div class="relative mt-8 mb-8">
            <div class="absolute inset-0 flex items-center" aria-hidden="true">
              <div class="w-full border-t border-gray-200"></div>
            </div>
            <div class="relative flex justify-center">
              <span class="bg-white px-4 font-medium text-gray-500">{{ $t('or') }}</span>
            </div>
          </div>
          <!-- Containers for Card Fields hosted by PayPal -->
          <div id="card-form" class="card_container">
            <div id="card-number-field-container"></div>
            <div class="grid grid-cols-2 gap-2">
              <div id="card-expiry-field-container"></div>
              <div id="card-cvv-field-container"></div>
            </div>
            <div id="card-name-field-container"></div>
            <Button type="button" class="py-4 !rounded-full mt-4 w-full" @click="handleCardPayment">
              {{ $t('Pay now with card') }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
